import * as React from 'react';

import {fetchProductWeekDetails} from '../../../gatsbyLib/data/fetchProductWeekDetails';
import {CarouselV2ProductDetailsResponse} from '../../contentful/recipeCarouselV2/utils/mapRecipeCarouselV2';

const cache = new Map<string, Promise<CarouselV2ProductDetailsResponse>>();
const resolved = new Map<string, CarouselV2ProductDetailsResponse>();

const cacheKey = (sku: string, week: string) => `${sku}|${week}`;

export const getProductWeekDetails = (sku: string, week: string): Promise<CarouselV2ProductDetailsResponse> => {
  const key = cacheKey(sku, week);
  const cached = cache.get(key);
  if (cached) {
    return cached;
  }

  const request = fetchProductWeekDetails(sku, week)
    .then(data => {
      resolved.set(key, data);

      return data;
    })
    .catch(e => {
      cache.delete(key);
      throw e;
    });
  cache.set(key, request);

  return request;
};

export interface RecipeCarouselV2DataState {
  data?: CarouselV2ProductDetailsResponse;
  isLoading: boolean;
  hasError: boolean;
}

const resolvedState = (sku?: string, week?: string): RecipeCarouselV2DataState | undefined => {
  const data = sku && week ? resolved.get(cacheKey(sku, week)) : undefined;

  return data && {data, isLoading: false, hasError: false};
};

export const useRecipeCarouselV2Data = (sku?: string, week?: string): RecipeCarouselV2DataState => {
  const [state, setState] = React.useState<RecipeCarouselV2DataState>(
    () => resolvedState(sku, week) ?? {isLoading: Boolean(sku && week), hasError: false}
  );

  React.useEffect(() => {
    if (!sku || !week) {
      setState({data: undefined, isLoading: false, hasError: false});

      return;
    }

    const known = resolvedState(sku, week);
    if (known) {
      setState(known);

      return;
    }

    let active = true;
    setState(s => ({...s, isLoading: true, hasError: false}));

    getProductWeekDetails(sku, week)
      .then(data => {
        if (active) {
          setState({data, isLoading: false, hasError: false});
        }
      })
      .catch(e => {
        // eslint-disable-next-line no-console
        console.log(`Failed to load recipe carousel. ${e.message}`);
        if (active) {
          setState({data: undefined, isLoading: false, hasError: true});
        }
      });

    return () => {
      active = false;
    };
  }, [sku, week]);

  return state;
};
